/**
 * The Stat Tracker table: search, category filter, sortable columns, and the
 * custom-field block broken out below the built-in catalog.
 *
 * Custom fields (Task 22 populates them from the tenant's stat schema) arrive
 * on the same feed under the `Custom` category with `is_custom: true`. They get
 * their own block rather than extra columns because the API models them as
 * event-level aggregates — one value per field per game, exactly like a
 * built-in stat — so a column per field would be a column of one cell. Until a
 * tenant defines any, the block renders its empty state so the surface is
 * visible and obviously not broken.
 *
 * A stat sheet is deliberately NOT paginated: FR-29 asks for *all* categories at
 * once, and a coach scanning for one number should not have to find its page.
 * Search + category filter do the narrowing instead.
 */
import { useMemo } from 'react';

import { formatStatValue, toStatListEntries } from '../summary/stat-block';

import type { SortField, StatSheetRow } from './use-tracker';

const SORT_LABELS: Record<SortField, string> = {
  category: 'Category',
  name: 'Stat',
  value: 'Value',
};

function numericValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (Array.isArray(value)) return value.length;
  return null;
}

function matchesSearch(row: StatSheetRow, needle: string): boolean {
  if (needle === '') return true;
  const haystack = `${row.name} ${row.category} ${row.key}`.toLowerCase();
  return haystack.includes(needle.toLowerCase());
}

export function filterAndSortRows(
  rows: readonly StatSheetRow[],
  options: { search: string; category: string; sortField: SortField; isDescending: boolean }
): StatSheetRow[] {
  const filtered = rows.filter(
    (row) =>
      matchesSearch(row, options.search.trim()) &&
      (options.category === '' || row.category === options.category)
  );
  const direction = options.isDescending ? -1 : 1;
  return [...filtered].sort((left, right) => {
    if (options.sortField === 'value') {
      const a = numericValue(left.value);
      const b = numericValue(right.value);
      if (a === null && b === null) return left.name.localeCompare(right.name);
      if (a === null) return 1;
      if (b === null) return -1;
      return (a - b) * direction;
    }
    if (options.sortField === 'name') return left.name.localeCompare(right.name) * direction;
    const byCategory = left.category.localeCompare(right.category) * direction;
    return byCategory !== 0 ? byCategory : left.name.localeCompare(right.name);
  });
}

function StatValueCell({ row }: { row: StatSheetRow }): JSX.Element {
  if (row.format === 'list') {
    const entries = toStatListEntries(row.value);
    return (
      <span className="stat-sheet__value">
        <span className="num">{entries.length}</span>
        {entries.length === 0 ? null : (
          <span className="stat-sheet__detail">
            {entries.map((entry) => entry.label).join(', ')}
          </span>
        )}
      </span>
    );
  }
  if (row.format === 'enum' || row.format === 'tag') {
    const record = typeof row.value === 'object' && row.value !== null ? row.value : {};
    const pairs = Object.entries(record as Record<string, unknown>);
    return (
      <span className="stat-sheet__value">
        {pairs.length === 0
          ? '—'
          : pairs.map(([label, count]) => `${label}: ${String(count)}`).join(' · ')}
      </span>
    );
  }
  return <span className="stat-sheet__value num">{formatStatValue(row.value, row.format)}</span>;
}

interface SortableHeaderProps {
  field: SortField;
  sortField: SortField;
  isDescending: boolean;
  onSortChange: (field: SortField) => void;
  isNumeric?: boolean;
}

function SortableHeader({
  field,
  sortField,
  isDescending,
  onSortChange,
  isNumeric = false,
}: SortableHeaderProps): JSX.Element {
  const isActive = sortField === field;
  return (
    <th
      scope="col"
      className={isNumeric ? 'stat-sheet__head stat-sheet__head--num' : 'stat-sheet__head'}
      aria-sort={isActive ? (isDescending ? 'descending' : 'ascending') : 'none'}
    >
      <button
        type="button"
        className="stat-sheet__sort"
        onClick={() => onSortChange(field)}
        data-testid={`stat-sheet-sort-${field}`}
      >
        {SORT_LABELS[field]}
        <span aria-hidden="true">{isActive ? (isDescending ? ' ▼' : ' ▲') : ''}</span>
      </button>
    </th>
  );
}

export interface StatSheetProps {
  rows: readonly StatSheetRow[];
  categories: readonly string[];
  search: string;
  category: string;
  sortField: SortField;
  isDescending: boolean;
  isLoading: boolean;
  /** Season view adds a "Per Game" column. */
  hasPerGameColumn: boolean;
  onSearchChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  /** One call so both params clear in a single URL write. */
  onClearFilters: () => void;
  onSortChange: (field: SortField) => void;
}

export function StatSheet({
  rows,
  categories,
  search,
  category,
  sortField,
  isDescending,
  isLoading,
  hasPerGameColumn,
  onSearchChange,
  onCategoryChange,
  onClearFilters,
  onSortChange,
}: StatSheetProps): JSX.Element {
  const visible = useMemo(
    () => filterAndSortRows(rows, { search, category, sortField, isDescending }),
    [rows, search, category, sortField, isDescending]
  );
  const builtIn = visible.filter((row) => !row.isCustom);
  const custom = visible.filter((row) => row.isCustom);
  const hasFilters = search.trim() !== '' || category !== '';

  return (
    <div className="stat-sheet">
      <div className="stat-sheet__controls">
        <label className="stat-sheet__field">
          <span>Find a stat</span>
          <input
            type="search"
            value={search}
            placeholder="e.g. completion, punt, third down"
            data-testid="stat-sheet-search"
            onChange={(changeEvent) => onSearchChange(changeEvent.target.value)}
          />
        </label>
        <label className="stat-sheet__field">
          <span>Category</span>
          <select
            value={category}
            data-testid="stat-sheet-category"
            onChange={(changeEvent) => onCategoryChange(changeEvent.target.value)}
          >
            <option value="">All categories</option>
            {categories.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        {hasFilters ? (
          <button
            type="button"
            className="btn btn--ghost"
            data-testid="stat-sheet-clear"
            onClick={onClearFilters}
          >
            Clear filters
          </button>
        ) : null}
      </div>

      {isLoading && rows.length === 0 ? (
        <p className="stat-sheet__empty" role="status">
          Loading stats…
        </p>
      ) : builtIn.length === 0 ? (
        <p className="stat-sheet__empty" data-testid="stat-sheet-empty">
          {rows.length === 0
            ? 'No stats yet. They appear as plays are logged for this unit.'
            : 'No stats match these filters.'}
        </p>
      ) : (
        <div className="stat-tracker__scroller">
          <table className="stat-sheet__table" data-testid="stat-sheet-table">
            <caption className="visually-hidden">
              Every derived statistic for the selected unit
            </caption>
            <thead>
              <tr>
                <SortableHeader
                  field="category"
                  sortField={sortField}
                  isDescending={isDescending}
                  onSortChange={onSortChange}
                />
                <SortableHeader
                  field="name"
                  sortField={sortField}
                  isDescending={isDescending}
                  onSortChange={onSortChange}
                />
                <SortableHeader
                  field="value"
                  sortField={sortField}
                  isDescending={isDescending}
                  onSortChange={onSortChange}
                  isNumeric
                />
                {hasPerGameColumn ? (
                  <th scope="col" className="stat-sheet__head stat-sheet__head--num">
                    Per Game
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {builtIn.map((row) => (
                <tr key={row.key} data-stat-key={row.key}>
                  <td className="stat-sheet__category">{row.category}</td>
                  <th scope="row" className="stat-sheet__name">
                    {row.name}
                  </th>
                  <td className="stat-sheet__cell--num">
                    <StatValueCell row={row} />
                  </td>
                  {hasPerGameColumn ? (
                    <td className="stat-sheet__cell--num num">
                      {row.perGame === null ? '—' : row.perGame.toFixed(1)}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <section className="stat-sheet__custom" aria-label="Custom stat fields">
        <h2 className="stat-sheet__custom-title">Custom fields</h2>
        {custom.length === 0 ? (
          <p className="stat-sheet__empty" data-testid="custom-fields-empty">
            No custom stat fields are defined for this team yet. Fields your Team Admin adds to the
            stat schema appear here automatically, aggregated the same way as built-in stats.
          </p>
        ) : (
          <div className="stat-tracker__scroller">
            <table className="stat-sheet__table" data-testid="custom-fields-table">
              <thead>
                <tr>
                  {custom.map((row) => (
                    <th scope="col" key={row.key} className="stat-sheet__head">
                      {row.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {custom.map((row) => (
                    <td key={row.key} data-stat-key={row.key}>
                      <StatValueCell row={row} />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="stat-sheet__count">
        {visible.length} of {rows.length} {rows.length === 1 ? 'stat' : 'stats'}
        {hasFilters ? ' (filtered)' : ''}
      </p>
    </div>
  );
}
